// Uses the existing GET /v1/config/google-maps-key endpoint.
// Do not change Google API controllers/routes.
import apiPath from "utils/pathObj";
import { apiGet } from "utils/apiFetch";

let googleMapsPromise = null;
let apiKeyPromise = null;

const fetchGoogleMapsApiKey = async () => {
  if (apiKeyPromise) return apiKeyPromise;

  apiKeyPromise = (async () => {
    const response = await apiGet(apiPath.getGoogleMapsKey);
    if (response?.data?.success && response?.data?.results?.apiKey) {
      return response.data.results.apiKey;
    }
    throw new Error("Failed to fetch Google Maps API key");
  })();

  return apiKeyPromise;
};

export const loadGoogleMaps = async () => {
  if (window.google?.maps?.places?.PlaceAutocompleteElement) return window.google;
  if (window.google?.maps?.importLibrary) {
    try {
      await window.google.maps.importLibrary("places");
      if (window.google.maps.places?.PlaceAutocompleteElement) return window.google;
    } catch (err) {
      console.error("Google places library import failed", err);
    }
  }

  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = (async () => {
    try {
      const apiKey = await fetchGoogleMapsApiKey();
      if (!document.getElementById("google-maps-script")) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.id = "google-maps-script";
          script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,maps&v=weekly`;
          script.async = true;
          script.defer = true;
          script.onload = () => {
            let tries = 0;
            const check = () => {
              if (window.google?.maps?.places?.PlaceAutocompleteElement) {
                resolve();
                return;
              }
              tries += 1;
              if (tries > 40 && window.google?.maps?.places) {
                resolve();
                return;
              }
              setTimeout(check, 50);
            };
            check();
          };
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      if (window.google?.maps?.importLibrary && !window.google.maps.places?.PlaceAutocompleteElement) {
        await window.google.maps.importLibrary("places");
      }
      if (!window.google?.maps) throw new Error("Google Maps failed to load");
      return window.google;
    } catch (error) {
      googleMapsPromise = null;
      throw error;
    }
  })();

  return googleMapsPromise;
};

export const parseAddressComponents = (components = []) => {
  const parsed = {
    streetNumber: "",
    streetName: "",
    city: "",
    state: "",
    stateName: "",
    postalCode: "",
    country: "",
    countryName: "",
  };
  (components || []).forEach((component) => {
    const types = component.types || (component.type ? [component.type] : []);
    const longText = component.longText || component.long_name || "";
    const shortText = component.shortText || component.short_name || "";
    if (types.includes("street_number")) parsed.streetNumber = longText;
    if (types.includes("route")) parsed.streetName = longText;
    if (types.includes("locality") && !parsed.city) parsed.city = longText;
    if (types.includes("postal_town") && !parsed.city) parsed.city = longText;
    if (types.includes("sublocality") && !parsed.city) parsed.city = longText;
    if (types.includes("sublocality_level_1") && !parsed.city) parsed.city = longText;
    if (types.includes("neighborhood") && !parsed.city) parsed.city = longText;
    if (types.includes("administrative_area_level_2") && !parsed.city) parsed.city = longText;
    if (types.includes("administrative_area_level_1")) {
      parsed.state = shortText || parsed.state;
      parsed.stateName = longText || parsed.stateName;
    }
    if (types.includes("postal_code")) parsed.postalCode = longText || shortText;
    if (types.includes("country")) {
      parsed.country = shortText || parsed.country;
      parsed.countryName = longText || parsed.countryName;
    }
  });
  return parsed;
};

export const fetchPlaceSuggestions = async (input) => {
  const query = String(input || "").trim();
  if (query.length < 2) return [];
  const google = await loadGoogleMaps();

  if (google.maps.places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
    const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: query,
    });
    return (suggestions || [])
      .map((item) => {
        const pred = item.placePrediction;
        if (!pred) return null;
        return {
          id: pred.placeId || pred.text?.toString(),
          label: pred.text?.toString() || "",
          prediction: pred,
        };
      })
      .filter((row) => row?.label);
  }

  return new Promise((resolve) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: query }, (results, status) => {
      if (status !== "OK" || !results?.length) {
        resolve([]);
        return;
      }
      resolve(
        results.slice(0, 8).map((row) => ({
          id: row.place_id,
          label: row.formatted_address,
          geocodeResult: row,
        })),
      );
    });
  });
};

export const geocodeAddress = async (address) => {
  const google = await loadGoogleMaps();
  const geocoder = new google.maps.Geocoder();
  return new Promise((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const location = results[0].geometry.location;
        resolve({
          latitude: location.lat(),
          longitude: location.lng(),
          formattedAddress: results[0].formatted_address || "",
          addressComponents: parseAddressComponents(results[0].address_components),
        });
        return;
      }
      reject(new Error(status || "ZERO_RESULTS"));
    });
  });
};

export const reverseGeocode = async (latitude, longitude) => {
  const google = await loadGoogleMaps();
  const geocoder = new google.maps.Geocoder();
  return new Promise((resolve, reject) => {
    geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const location = results[0].geometry.location;
        resolve({
          latitude: location.lat(),
          longitude: location.lng(),
          formattedAddress: results[0].formatted_address || "",
        });
        return;
      }
      reject(new Error(status || "ZERO_RESULTS"));
    });
  });
};
